/**
 * MetadataSection — Section 1 of QuestionEditForm.
 *
 * Renders the metadata block: teacher banner (admin/teacher questions
 * only), subject + chapter/topic, type + difficulty + marks, admin-only
 * readiness + enrich-status, target exams, pool membership, and admin-only
 * appeared-in-exams editor.
 *
 * State is owned by the parent (`QuestionEditForm`); this is presentational.
 * Extracted from `QuestionEditForm.tsx` during the 2026-05-17 split.
 */
import { useState, type ReactNode } from 'react';
import type { ExamAppearance } from '../../questions-page';
import type { ModuleItem } from '../../reference-data/types';
import { subjectDisplayName } from '../../reference-data';
import { TopicSelector } from '../../components/TopicSelector';
import { DatePicker } from '../../components/DatePicker';
import {
    READINESS_LEVELS,
    ENRICH_STATUS_LEVELS,
    DIFFICULTY_OPTIONS,
} from '../../question-set-types';
import { BoardGradePicker } from './BoardGradePicker';
import {
    PROGRAMME_OPTIONS,
    QUESTION_TYPES,
    QUESTION_TYPE_LABELS,
    gradesFromBoardGradeKeys,
    boardsFromBoardGradeKeys,
} from '@padcanvas/shared';
import {
    VALID_READINESS_VALUES,
    type ConceptOption,
    type QuestionFormData,
    type QuestionType,
    type UpdateField,
} from '../formTypes';
import { ConceptTagPicker } from './ConceptTagPicker';

const QUESTION_TYPE_OPTIONS = QUESTION_TYPES;

export interface MetadataSectionProps {
    form: QuestionFormData;
    updateField: UpdateField;
    isAdmin?: boolean;
    teacherId?: string;
    teacherEmail?: string;
    subjects: ReadonlyArray<string>;
    modulesBySubject?: Record<string, ModuleItem[]>;
    /** Exam appearances handlers (admin only). */
    addExamAppearance: () => void;
    removeExamAppearance: (idx: number) => void;
    handleExamAppearanceChange: (idx: number, field: keyof ExamAppearance, value: string) => void;
    /** Marks change on an exam appearance — typed separately because marks is a number. */
    handleExamAppearanceMarks: (idx: number, marks: number | undefined) => void;
    /** Concept registry for this subject (Phase 2). When provided (admin),
     *  renders the question-level concept-tagging picker. */
    conceptOptions?: ConceptOption[];
    /** Admin: render a live curricula-membership panel from the current module /
     *  class / target-exam values. Shown just above "Appeared In Exams". */
    renderCurriculaPreview?: (args: { moduleId?: string; classes?: string[]; targetExams?: string[] }) => ReactNode;
}

export function MetadataSection({
    form,
    updateField,
    isAdmin,
    teacherId,
    teacherEmail,
    subjects,
    modulesBySubject,
    addExamAppearance,
    removeExamAppearance,
    handleExamAppearanceChange,
    handleExamAppearanceMarks,
    conceptOptions,
    renderCurriculaPreview,
}: MetadataSectionProps) {
    // Concepts subsection starts expanded when the question already has tags,
    // so existing tags are visible; otherwise collapsed (advanced/optional).
    const [conceptsOpen, setConceptsOpen] = useState<boolean>(
        () => !!(form.usesConcepts?.length || form.avoidConcepts?.length),
    );
    return (
        <div className="form-section-group">
            <div className="form-section-group-header">Metadata</div>
            <div className="form-section-group-body">
                {/* Teacher info (read-only, admin only) */}
                {isAdmin && teacherId && (
                    <div style={{ padding: '8px 12px', marginBottom: 12, background: 'rgba(59,130,246,0.08)', borderRadius: 6, fontSize: 13, color: 'var(--text-secondary)' }}>
                        <strong>Teacher:</strong> {teacherEmail || teacherId}
                    </div>
                )}

                {/* Subject | Module */}
                <div className="form-row-2">
                    <div className="form-section">
                        <label>Subject</label>
                        <select
                            value={form.subject}
                            onChange={e => updateField('subject', e.target.value)}
                        >
                            <option value="">Select subject</option>
                            {subjects.map(s => (
                                <option key={s} value={s}>{subjectDisplayName(s)}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-section">
                        <label>Chapter / Topic</label>
                        <TopicSelector
                            // Topic only. The write path refuses a question filed on a
                            // chapter or a top-level module — "never directly on a
                            // chapter", admin and teacher editors alike — so offering
                            // those here is offering a choice that comes back as a
                            // save error the reader cannot act on.
                            selectableLevels={['topic']}
                            modules={(modulesBySubject || {})[form.subject] || []}
                            value={form.module?.id || ''}
                            onChange={v => {
                                const m = ((modulesBySubject || {})[form.subject] || []).find(x => x.id === v);
                                updateField('module', v ? { id: v, name: m?.displayName ?? '' } : undefined);
                            }}
                            placeholder="Search chapters & topics..."
                        />
                    </div>
                </div>

                {/* Question Type | Difficulty | Marks */}
                <div className="form-row-3">
                    <div className="form-section">
                        <label>Question Type</label>
                        <select
                            value={form.questionType || ''}
                            onChange={e => updateField('questionType', (e.target.value || undefined) as QuestionType | undefined)}
                        >
                            <option value="">Not specified</option>
                            {QUESTION_TYPE_OPTIONS.map(type => (
                                <option key={type} value={type}>{QUESTION_TYPE_LABELS[type]}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-section">
                        <label>Difficulty</label>
                        <select
                            value={form.difficulty || 'medium'}
                            onChange={e => updateField('difficulty', e.target.value)}
                        >
                            {DIFFICULTY_OPTIONS.map(d => (
                                <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-section">
                        <label>Marks</label>
                        {form.questionType === 'STRUCTURED' ? (
                            <input
                                type="text"
                                disabled
                                value={(() => {
                                    const total = (form.parts || []).reduce((s, p) => s + (p.marks ?? 0), 0);
                                    return total > 0 ? `${total} (Σ parts)` : '— (set per part)';
                                })()}
                                title="Total marks = sum of part marks for STRUCTURED questions"
                            />
                        ) : (
                            <input
                                type="number"
                                min="1"
                                step="1"
                                value={form.marks ?? ''}
                                onChange={e => updateField('marks', e.target.value === '' ? undefined : parseInt(e.target.value, 10))}
                                placeholder="e.g. 4"
                            />
                        )}
                    </div>
                </div>

                {/* Question Readiness | Enrich Status (admin only) */}
                {isAdmin && (
                    <div className="form-row-2">
                        <div className="form-section">
                            <label>Question Readiness</label>
                            <select
                                value={VALID_READINESS_VALUES.has(form.questionReadiness ?? -1) ? form.questionReadiness : 0}
                                onChange={e => updateField('questionReadiness', parseInt(e.target.value, 10))}
                            >
                                {READINESS_LEVELS.map(r => (
                                    <option key={r.value} value={r.value}>{r.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="form-section">
                            <label>Enrich Status</label>
                            <select
                                value={form.enrichStatus ?? 0}
                                onChange={e => updateField('enrichStatus', parseInt(e.target.value, 10))}
                            >
                                {ENRICH_STATUS_LEVELS.map(o => (
                                    <option key={o.value} value={o.value}>{o.value}: {o.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}

                {/* Pool membership — the ONLY control over who a question serves.
                    It replaced the Class checkboxes, and then the Target Exams ones:
                    both were separate editors for the grade and board halves of these
                    same keys, and neither is stored any more. Two controls over one
                    fact is how 3315 questions ended up disagreeing with themselves. */}
                <div className="form-section">
                    <label>Serves (board × grade)</label>
                    <BoardGradePicker
                        value={form.boardGrades ?? []}
                        onChange={next => updateField('boardGrades', next)}
                    />
                </div>

                {/* Exam Appearances (admin only) */}
                {isAdmin && (
                    <div className="form-section">
                        <div className="section-header">
                            <label>Appeared In Exams</label>
                            <button type="button" className="btn-add-inline" onClick={addExamAppearance}>+ Add</button>
                        </div>
                        {(form.appearedInExams || []).map((appearance, idx) => (
                            <div key={idx} className="appearance-editor">
                                <select
                                    value={appearance.exam}
                                    onChange={e => handleExamAppearanceChange(idx, 'exam', e.target.value)}
                                >
                                    {PROGRAMME_OPTIONS.map(exam => (
                                        <option key={exam} value={exam}>{exam}</option>
                                    ))}
                                </select>
                                <DatePicker
                                    value={appearance.date}
                                    ariaLabel="Exam date"
                                    onChange={v => handleExamAppearanceChange(idx, 'date', v)}
                                />
                                {appearance.exam === 'JEE' ? (
                                    // JEE runs two daily shifts — restrict session to a canonical shift.
                                    <select
                                        value={appearance.session}
                                        onChange={e => handleExamAppearanceChange(idx, 'session', e.target.value)}
                                        style={{ width: '130px' }}
                                    >
                                        <option value="">Session…</option>
                                        <option value="Shift 1">Shift 1</option>
                                        <option value="Shift 2">Shift 2</option>
                                    </select>
                                ) : (
                                    <input
                                        type="text"
                                        value={appearance.session}
                                        onChange={e => handleExamAppearanceChange(idx, 'session', e.target.value.slice(0, 16))}
                                        placeholder="Session"
                                        maxLength={16}
                                        style={{ width: '130px' }}
                                    />
                                )}
                                <input
                                    type="text"
                                    value={appearance.questionNo}
                                    onChange={e => handleExamAppearanceChange(idx, 'questionNo', e.target.value)}
                                    placeholder="Q#"
                                    className="qno-input"
                                />
                                <input
                                    type="number"
                                    min="1"
                                    step="1"
                                    value={appearance.marks ?? ''}
                                    onChange={e => handleExamAppearanceMarks(idx, e.target.value === '' ? undefined : parseInt(e.target.value, 10))}
                                    placeholder="Marks"
                                    className="marks-input"
                                />
                                <button className="btn-remove-inline" onClick={() => removeExamAppearance(idx)}>x</button>
                            </div>
                        ))}
                    </div>
                )}

                {/* Live curricula membership from the current module and the Serves
                    matrix. Shown below "Appeared In Exams"; the panel is collapsible.

                    Derived from `boardGrades`, not the retired class / targetExams
                    the form still carries: those are stripped before storage, so a
                    preview reading them showed the saved question's mismatch badges
                    disagreeing with the matrix right above it. */}
                {renderCurriculaPreview && (
                    <div className="form-section">
                        {renderCurriculaPreview({
                            moduleId: form.module?.id,
                            classes: gradesFromBoardGradeKeys(form.boardGrades),
                            targetExams: boardsFromBoardGradeKeys(form.boardGrades),
                        })}
                    </div>
                )}

                {/* Concepts (Phase 2 tagging; admin/teacher — present iff conceptOptions supplied).
                    Collapsible subsection, kept LAST in Metadata. */}
                {conceptOptions && (
                    <div className="form-section" style={{ borderTop: '1px solid var(--border-color, rgba(255,255,255,0.1))', paddingTop: 12, marginTop: 4 }}>
                        <button
                            type="button"
                            onClick={() => setConceptsOpen(o => !o)}
                            aria-expanded={conceptsOpen}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 6, width: '100%',
                                background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                                // Match the .form-section > label subtitle style.
                                fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)',
                            }}
                        >
                            <span style={{ display: 'inline-block', width: 12, transition: 'transform 0.15s', transform: conceptsOpen ? 'rotate(90deg)' : 'none' }}>▸</span>
                            Concepts
                        </button>

                        {conceptsOpen && (
                            <div style={{ marginTop: 12 }}>
                                <div className="form-section">
                                    <label>Concepts used</label>
                                    <ConceptTagPicker
                                        value={form.usesConcepts || []}
                                        onChange={ids => updateField('usesConcepts', ids.length ? ids : undefined)}
                                        concepts={conceptOptions}
                                        placeholder="Tag the concepts this question uses…"
                                    />
                                </div>

                                {/* L3 hard override: always avoided in hints, regardless of the
                                    student's frontier (docs/FeatureDesigns/HintSystem/grade-appropriate-hints.md) */}
                                <div className="form-section">
                                    <label>Avoid concepts (always avoid in hints)</label>
                                    <ConceptTagPicker
                                        value={form.avoidConcepts || []}
                                        onChange={ids => updateField('avoidConcepts', ids.length ? ids : undefined)}
                                        concepts={conceptOptions}
                                        placeholder="Concepts hints must never use for this question…"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
